/**
 * Fusion tableau + zone retouchee, sans bord visible.
 *
 * Le compositing se fait directement dans le canvas ecran, sous la transformee
 * du viewport, et pas dans un canvas a la resolution de l'image : la zone ne
 * fait que ~80 px de large dans le tableau alors que le joueur dessine a
 * 1024 px. Passer par un composite a la taille de l'image ecraserait la
 * retouche a 80 px. En dessinant la couche de zone directement a l'ecran, le
 * navigateur l'echantillonne a la resolution reellement affichee.
 *
 * Deux mecanismes cachent la couture :
 *  1. le masque de la zone est adouci (feathering) sur FEATHER_PX pixels image ;
 *  2. un grain leger est applique sur TOUT le tableau composite, pour que
 *     l'œuvre et la retouche partagent la meme texture.
 */
import type { DrawZone } from '@retusch/shared';
import { type Rect, type Size, type Vec2, type Viewport, zoneToRect } from './geometry.js';
import {
  FEATHER_PX,
  ZONE_SOFTNESS_SOURCE_PX,
  GRAIN_ALPHA,
  GRAIN_AMPLITUDE,
  GRAIN_BLEND,
  GRAIN_SEED,
  GRAIN_TILE_SIZE,
} from './renderConfig.js';
import { dominantColor } from './dominantColor.js';

/**
 * Ce qui est peint dans le rectangle de la zone :
 *  - `retouched` : blanc + traits (le rendu de jeu) ;
 *  - `masked`    : blanc seul (zone non encore dessinee) ;
 *  - `artwork`   : RIEN, l'œuvre d'origine reste visible. C'est le seul mode
 *                  qui montre le tableau tel qu'il etait avant masquage : la
 *                  revelation en a besoin pour son curseur avant/apres.
 */
export type ZoneRender = 'retouched' | 'masked' | 'artwork';

export interface CompositeOptions {
  readonly grain: boolean;
  readonly feather: boolean;
  readonly zone?: ZoneRender;
}

/** Marge de prolongement, en multiples du rayon : couvre la queue du noyau. */
const PAD_FACTOR = 3;

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function context2d(
  canvas: HTMLCanvasElement,
  options?: CanvasRenderingContext2DSettings,
): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', options);
  if (ctx === null) throw new Error('contexte 2d indisponible');
  return ctx;
}

/** Bruit deterministe : le grain est le meme pour tous les joueurs. */
function buildGrainTile(): HTMLCanvasElement {
  const canvas = createCanvas(GRAIN_TILE_SIZE, GRAIN_TILE_SIZE);
  const ctx = context2d(canvas);
  const image = ctx.createImageData(GRAIN_TILE_SIZE, GRAIN_TILE_SIZE);
  const data = image.data;

  let state = GRAIN_SEED >>> 0;
  for (let i = 0; i < data.length; i += 4) {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const noise = (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
    const value = 128 + noise * GRAIN_AMPLITUDE;
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
    data[i + 3] = 255;
  }

  ctx.putImageData(image, 0, 0);
  return canvas;
}

export class Compositor {
  private readonly imageSize: Size;
  private readonly rect: Rect;

  /** Tableau d'origine, pour l'affichage. */
  private readonly artwork: HTMLCanvasElement;
  /**
   * Tableau avec la zone deja masquee en blanc. Sert de source a la pipette :
   * echantillonner l'œuvre d'origine sous le masque laisserait le joueur
   * reveler pixel par pixel ce qu'il est cense redessiner.
   */
  private readonly sampleSource: HTMLCanvasElement;
  private readonly sampleCtx: CanvasRenderingContext2D;

  private readonly zoneLayer: HTMLCanvasElement;
  private readonly zoneLayerCtx: CanvasRenderingContext2D;
  private featherMask: HTMLCanvasElement | null = null;
  private zoneLayerDirty = true;
  private zoneLayerFeathered = false;
  /**
   * Variante actuellement en cache. Sans ce champ, deux appels a draw() dans la
   * MEME frame avec des contenus differents (curseur avant/apres de la
   * revelation) recevaient tous les deux le premier calque construit : d'ou un
   * carre blanc des deux cotes.
   */
  private zoneLayerMode: ZoneRender | null = null;
  /** Facteur d'echelle calque/source, pour exprimer le flou en pixels source. */
  private readonly layerPerSourcePx: number;
  /** Copie des traits a bords prolonges, reutilisee entre les frames. */
  private padded: HTMLCanvasElement | null = null;

  private readonly grainTile: HTMLCanvasElement;
  private grainPattern: CanvasPattern | null = null;

  /**
   * Fond de la zone, la ou l'on posait du blanc.
   *
   * C'est la couleur la plus presente SOUS le masque (cf. dominantColor). Le
   * blanc n'existe presque jamais dans nos illustrations : il annoncait la zone
   * de loin, obligeait a tout repeindre avant de commencer a se cacher, et
   * faussait l'appreciation des couleurs voisines, qui est pourtant tout le
   * jeu.
   *
   * Il n'est PAS transmis : chaque client le recalcule de la meme image et de
   * la meme zone, comme il recalcule deja tout le rendu. Le protocole ne porte
   * que des traits (cf. CLAUDE.md), et une couleur de plus par dessin serait un
   * champ a valider, a versionner et a garder synchrone pour rien.
   */
  private readonly base: string;

  /** Fond de la zone : la couleur a repeindre pour effacer (cf. `base`). */
  get baseColor(): string {
    return this.base;
  }

  constructor(source: CanvasImageSource, imageSize: Size, zone: DrawZone, layerWidth: number) {
    this.imageSize = imageSize;
    this.rect = zoneToRect(zone, imageSize);

    this.artwork = createCanvas(imageSize.width, imageSize.height);
    context2d(this.artwork).drawImage(source, 0, 0, imageSize.width, imageSize.height);

    this.sampleSource = createCanvas(imageSize.width, imageSize.height);
    this.sampleCtx = context2d(this.sampleSource, { willReadFrequently: true });
    this.sampleCtx.drawImage(this.artwork, 0, 0);
    // AVANT de masquer : la couleur de fond se lit dans l'œuvre, a l'endroit
    // meme que le masque va recouvrir.
    this.base = dominantColor(this.sampleCtx, this.rect);
    this.sampleCtx.fillStyle = this.base;
    this.sampleCtx.fillRect(this.rect.x, this.rect.y, this.rect.width, this.rect.height);

    // Calque HAUTE RESOLUTION : comme le tableau, la retouche ne doit subir
    // qu'un seul reechantillonnage. L'alignement sur l'œuvre se fait par un
    // adoucissement calibre (cf. ZONE_SOFTNESS_SOURCE_PX), pas en jetant de la
    // resolution.
    const layerHeight = Math.round((layerWidth * this.rect.height) / this.rect.width);
    this.zoneLayer = createCanvas(layerWidth, layerHeight);
    this.zoneLayerCtx = context2d(this.zoneLayer);
    // Combien de pixels du calque vaut UN pixel du tableau source.
    this.layerPerSourcePx = layerWidth / Math.max(this.rect.width, 1);

    this.grainTile = buildGrainTile();
  }

  get zoneRect(): Rect {
    return this.rect;
  }

  /** A appeler des que le canvas de zone change (trait, undo, fill). */
  invalidateZone(): void {
    this.zoneLayerDirty = true;
  }

  /**
   * Couleur du tableau (zone masquee en blanc) sous un point normalise image.
   * Utilise par la pipette.
   */
  sampleColor(normalized: Vec2): string {
    const x = Math.min(
      Math.max(Math.round(normalized.x * this.imageSize.width), 0),
      this.imageSize.width - 1,
    );
    const y = Math.min(
      Math.max(Math.round(normalized.y * this.imageSize.height), 0),
      this.imageSize.height - 1,
    );
    const [r, g, b] = this.sampleCtx.getImageData(x, y, 1, 1).data;
    const hex = (value: number | undefined): string =>
      (value ?? 0).toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
  }

  /**
   * Dessine tableau + zone dans `ctx` (deja a l'echelle dpr, en px CSS).
   * `strokes` est le canvas de traits de ZoneCanvas, ou null pour une zone
   * encore vierge.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    strokes: HTMLCanvasElement | null,
    options: CompositeOptions,
  ): void {
    const { width, height } = this.imageSize;

    ctx.save();
    ctx.translate(viewport.tx, viewport.ty);
    ctx.scale(viewport.scale, viewport.scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(this.artwork, 0, 0, width, height);

    // `artwork` ne pose aucun calque : l'œuvre d'origine reste intacte.
    const mode: ZoneRender = options.zone ?? 'retouched';
    if (mode !== 'artwork') {
      const layer = this.buildZoneLayer(strokes, options.feather, mode);
      ctx.drawImage(layer, this.rect.x, this.rect.y, this.rect.width, this.rect.height);
    }

    ctx.restore();

    if (options.grain) this.drawGrain(ctx, viewport);
  }

  /**
   * Pose UNIQUEMENT la zone, sans repeindre l'œuvre.
   *
   * `draw` repeint le tableau entier a chaque appel, ce qui va tres bien tant
   * qu'il n'y a qu'une retouche. La chasse du battle royale en empile
   * plusieurs : le deuxieme appel effacait le premier, et une seule retouche
   * survivait a l'ecran. Ici l'appelant peint le fond une fois, puis appelle
   * ceci pour chaque piece.
   */
  drawZoneOn(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    strokes: HTMLCanvasElement | null,
    feather: boolean,
  ): void {
    const layer = this.buildZoneLayer(strokes, feather, 'retouched');
    ctx.save();
    ctx.translate(viewport.tx, viewport.ty);
    ctx.scale(viewport.scale, viewport.scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(layer, this.rect.x, this.rect.y, this.rect.width, this.rect.height);
    ctx.restore();
  }

  /** Blanc du masque + traits, adouci sur les bords. */
  private buildZoneLayer(
    strokes: HTMLCanvasElement | null,
    feather: boolean,
    mode: ZoneRender,
  ): HTMLCanvasElement {
    if (
      !this.zoneLayerDirty &&
      this.zoneLayerFeathered === feather &&
      this.zoneLayerMode === mode
    ) {
      return this.zoneLayer;
    }

    const ctx = this.zoneLayerCtx;
    const { width, height } = this.zoneLayer;

    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = this.base;
    ctx.fillRect(0, 0, width, height);

    if (mode === 'retouched' && strokes !== null) {
      // Adoucissement des TRAITS seuls : le fond du masque en dessous reste
      // uni, il n'a aucun bord a etaler. Le rayon est converti en pixels de
      // calque, ce qui le rend independant du zoom et de la taille de zone.
      const radius = ZONE_SOFTNESS_SOURCE_PX * this.layerPerSourcePx;
      if (radius > 0.05) {
        // Les bords sont PROLONGES avant le flou. Sans cela le noyau
        // echantillonne le vide au-dela du canvas des traits : leur opacite
        // decroit sur le pourtour, le fond du masque reapparait, et on voit un
        // liseré tout autour de la zone.
        ctx.filter = `blur(${radius.toFixed(2)}px)`;
        ctx.drawImage(this.padStrokes(strokes, radius), -PAD_FACTOR * radius, -PAD_FACTOR * radius);
        ctx.filter = 'none';
      } else {
        ctx.drawImage(strokes, 0, 0, width, height);
      }
    }

    if (feather && FEATHER_PX > 0) {
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(this.getFeatherMask(), 0, 0);
    }
    ctx.restore();

    this.zoneLayerDirty = false;
    this.zoneLayerFeathered = feather;
    this.zoneLayerMode = mode;
    return this.zoneLayer;
  }

  /**
   * Copie des traits avec les bords PROLONGES vers l'exterieur, pour que le
   * flou ait de la matiere a echantillonner au-dela de la zone.
   *
   * L'equivalent d'un `clamp-to-edge` : on etire le dernier rang de pixels sur
   * la marge. C'est une approximation (le vrai voisinage, c'est l'œuvre en
   * dessous, que le calque ne connait pas), mais elle suffit : elle empeche
   * l'opacite de tomber sur le pourtour, ce qui est tout ce qu'on demande.
   */
  private padStrokes(strokes: HTMLCanvasElement, radius: number): HTMLCanvasElement {
    const pad = Math.ceil(PAD_FACTOR * radius);
    const { width, height } = this.zoneLayer;
    const target = this.padded ?? createCanvas(width + pad * 2, height + pad * 2);
    if (target.width !== width + pad * 2 || target.height !== height + pad * 2) {
      target.width = width + pad * 2;
      target.height = height + pad * 2;
    }
    this.padded = target;

    const ctx = context2d(target);
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const sw = strokes.width;
    const sh = strokes.height;
    // Contenu, puis les quatre bandes et les quatre coins.
    ctx.drawImage(strokes, pad, pad, width, height);
    ctx.drawImage(strokes, 0, 0, sw, 1, pad, 0, width, pad);
    ctx.drawImage(strokes, 0, sh - 1, sw, 1, pad, pad + height, width, pad);
    ctx.drawImage(strokes, 0, 0, 1, sh, 0, pad, pad, height);
    ctx.drawImage(strokes, sw - 1, 0, 1, sh, pad + width, pad, pad, height);
    ctx.drawImage(strokes, 0, 0, 1, 1, 0, 0, pad, pad);
    ctx.drawImage(strokes, sw - 1, 0, 1, 1, pad + width, 0, pad, pad);
    ctx.drawImage(strokes, 0, sh - 1, 1, 1, 0, pad + height, pad, pad);
    ctx.drawImage(strokes, sw - 1, sh - 1, 1, 1, pad + width, pad + height, pad, pad);
    return target;
  }

  /**
   * Masque alpha : plein au centre, degrade sur les bords. Le rayon est exprime
   * en pixels image puis converti a l'echelle du calque, pour que l'adoucissement
   * represente toujours FEATHER_PX pixels du tableau.
   */
  private getFeatherMask(): HTMLCanvasElement {
    if (this.featherMask !== null) return this.featherMask;

    const { width, height } = this.zoneLayer;
    const scale = width / this.rect.width;
    const radius = Math.max(1, FEATHER_PX * scale);

    const mask = createCanvas(width, height);
    const ctx = context2d(mask);
    ctx.filter = `blur(${(radius / 2).toFixed(2)}px)`;
    ctx.fillStyle = '#000000';
    ctx.fillRect(radius, radius, width - radius * 2, height - radius * 2);
    ctx.filter = 'none';

    this.featherMask = mask;
    return mask;
  }

  /**
   * Grain applique de l'exterieur, sur un composite deja assemble.
   *
   * La chasse du battle royale empile plusieurs retouches sur un meme tableau :
   * le grain doit passer UNE fois sur l'ensemble, sinon chaque piece porte sa
   * propre texture et se repere a ca plutot qu'a son contenu.
   */
  drawGrainOn(ctx: CanvasRenderingContext2D, viewport: Viewport): void {
    this.drawGrain(ctx, viewport);
  }

  /**
   * Grain en espace ecran : taille constante quel que soit le zoom, et cadre sur
   * l'emprise du tableau pour ne pas deborder sur le fond de la galerie.
   */
  private drawGrain(ctx: CanvasRenderingContext2D, viewport: Viewport): void {
    this.grainPattern ??= ctx.createPattern(this.grainTile, 'repeat');
    if (this.grainPattern === null) return;

    const x = viewport.tx;
    const y = viewport.ty;
    const width = this.imageSize.width * viewport.scale;
    const height = this.imageSize.height * viewport.scale;

    ctx.save();
    ctx.globalCompositeOperation = GRAIN_BLEND;
    ctx.globalAlpha = GRAIN_ALPHA;
    ctx.fillStyle = this.grainPattern;
    ctx.fillRect(x, y, width, height);
    ctx.restore();
  }
}
